/**
 * SysAdmin Panel — separate subdirectory (/sysadmin) for global platform
 * administration. The SysAdmin manages Comercios (tenants) and their users.
 * Uses its OWN auth (super_admins) stored separately from the tenant login,
 * and every API call goes WITHOUT the x-tenant-slug header (global scope).
 */

import { useState, useEffect } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

function saFetch(path: string, opts: any = {}) {
  const token = localStorage.getItem('sysadminToken');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

/** Filtros del listado de ítems del catálogo. Vacío = sin filtrar. */
const emptyCatalogFilters = {
  verticalCode: '',
  q: '',
  gtin: '',
  sku: '',
  name: '',
  brand: '',
  category: '',
  saleUnit: '',
  purchaseUnit: '',
  active: '',
};

/** Valores presentes en los catálogos, para que los filtros sean desplegables. */
const emptyCatalogFacets = {
  verticals: [] as string[],
  categories: [] as string[],
  saleUnits: [] as string[],
  purchaseUnits: [] as string[],
};

/** Estilo compartido de los campos de filtro. */
const saInput = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm';

function logout() {
  localStorage.removeItem('sysadminToken');
  localStorage.removeItem('sysadminRefreshToken');
  localStorage.removeItem('sysadminUser');
  window.location.href = '/sysadmin/login';
}

export function SysAdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (localStorage.getItem('sysadminToken')) {
    window.location.href = '/sysadmin';
    return null;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await saFetch('/sysadmin/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al iniciar sesión');
      localStorage.setItem('sysadminToken', d.accessToken);
      localStorage.setItem('sysadminRefreshToken', d.refreshToken || '');
      localStorage.setItem('sysadminUser', JSON.stringify(d.user));
      window.location.href = '/sysadmin';
    } catch (e2: any) {
      setError(e2.message || 'Error al iniciar sesión');
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6">
        <h1 className="text-xl font-bold dark:text-gray-100">🛠️ Panel SysAdmin</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Administración global de Comercios</p>
        {error && <div className="bg-red-50 text-red-700 dark:text-red-300 p-3 rounded-lg mb-4 text-sm">⚠️ {error}</div>}
        <form onSubmit={submit} className="space-y-3">
          <input
            type="email" required placeholder="Email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <input
            type="password" required placeholder="Password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <button disabled={busy} className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold min-h-[44px]">
            {busy ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>
        <a href="/login" className="block text-center text-sm text-blue-600 dark:text-blue-400 mt-4">← Volver al login del comercio</a>
      </div>
    </div>
  );
}

/**
 * Alert destination for the signed-in sysadmin.
 *
 * Stored on super_admins.phone and read by the ClamAV health monitor. The
 * SYSADMIN_ALERT_PHONE value in .env is only a fallback for when the database
 * itself is unreachable, so editing the number here is never silently ignored.
 */
function AlertPhoneCard() {
  const [phone, setPhone] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await saFetch('/sysadmin/me');
        const d = await res.json().catch(() => ({}));
        if (!alive || !res.ok) return;
        const current = d.data?.phone || '';
        setPhone(current);
        setSaved(current);
      } catch {
        // Auxiliary card: a failed load must never break the panel.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const res = await saFetch('/sysadmin/me', { method: 'PUT', body: JSON.stringify({ phone }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'No se pudo guardar');
      const current = d.data?.phone || '';
      setPhone(current);
      setSaved(current);
      setMsg({ ok: true, text: current ? 'Número guardado' : 'Alertas desactivadas' });
    } catch (err: any) {
      setMsg({ ok: false, text: err.message || 'Error al guardar' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="pb-3 space-y-2">
      <p className="text-xs font-semibold text-gray-300">🔔 Alertas del sistema</p>
      <input
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="+5491127043909"
        className="w-full px-2 py-1.5 text-xs rounded bg-gray-800 border border-gray-600 text-gray-100 placeholder-gray-500"
      />
      <p className="text-[11px] leading-snug text-gray-500">
        WhatsApp donde recibís las alertas técnicas (ej. antivirus caído). Vacío = sin avisos.
      </p>
      <button
        type="submit"
        disabled={busy || phone === saved}
        className="w-full px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium"
      >
        {busy ? 'Guardando…' : 'Guardar número'}
      </button>
      {msg && <p className={`text-[11px] ${msg.ok ? 'text-green-400' : 'text-red-400'}`}>{msg.text}</p>}
    </form>
  );
}

export function SysAdminApp() {
  const token = localStorage.getItem('sysadminToken');
  const [user] = useState<any>(() => { try { return JSON.parse(localStorage.getItem('sysadminUser') || 'null'); } catch { return null; } });
  const [tenants, setTenants] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // create/edit form
  const [form, setForm] = useState({ email: '', name: '', role: 'cashier', password: '', google: false, phone: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [auditLogs, setAuditLogs] = useState<any[] | null>(null);
  // Estado de los catálogos generales por rubro (solo lectura)
  const [catalogs, setCatalogs] = useState<any[] | null>(null);
  /** Vertical abierto para inspeccionar sus ítems; null = viendo la lista. */
  const [catalogOpen, setCatalogOpen] = useState<string | null>(null);
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogFilters, setCatalogFilters] = useState<typeof emptyCatalogFilters>({ ...emptyCatalogFilters });
  const [catalogFacets, setCatalogFacets] = useState<typeof emptyCatalogFacets>({ ...emptyCatalogFacets });
  const [auditDays, setAuditDays] = useState(7);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showNewTenant, setShowNewTenant] = useState(false);
  const [newTenantForm, setNewTenantForm] = useState({ name: '', slug: '', taxRate: '21.00', currency: 'ARS' });
  const [newTenantBusy, setNewTenantBusy] = useState(false);

  if (!token) {
    window.location.href = '/sysadmin/login';
    return null;
  }

  async function loadTenants() {
    setError('');
    try {
      const res = await saFetch('/sysadmin/tenants');
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al cargar comercios');
      setTenants(d.data || []);
    } catch (e: any) {
      setError(e.message || 'Error');
    }
  }

  useEffect(() => { loadTenants(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function loadAudit() {
    setAuditLoading(true);
    setError('');
    try {
      const res = await saFetch(`/sysadmin/access-logs?days=${auditDays}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al cargar auditoría');
      setAuditLogs(d.data || []);
    } catch (e: any) {
      setAuditLogs([]);
      setError(e.message || 'Error al cargar auditoría');
    } finally {
      setAuditLoading(false);
    }
  }

  function openAudit() {
    setSelected(null);
    setAuditLogs([]);
    loadAudit();
  }

  async function loadCatalogs() {
    setError('');
    setSelected(null);
    setAuditLogs(null);
    setCatalogs([]);
    setCatalogOpen(null);
    try {
      const res = await saFetch('/sysadmin/catalog');
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al cargar catálogos');
      setCatalogs(d.data || []);
    } catch (e: any) {
      setError(e.message || 'Error al cargar catálogos');
    }
  }

  /**
   * Ítems del catálogo de un vertical, con filtro por cada campo.
   *
   * Los filtros se mandan al servidor en vez de filtrar en el navegador: un
   * catálogo real tiene miles de ítems y traerlos todos para filtrar acá no escala.
   */
  async function loadCatalogItems() {
    setCatalogBusy(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(catalogFilters)) if (v) qs.set(k, String(v));
      qs.set('limit', '100');
      const res = await saFetch(`/sysadmin/catalog/items?${qs.toString()}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al cargar los ítems del catálogo');
      setCatalogItems(d.data || []);
      setCatalogTotal(d.total ?? 0);
      setCatalogFacets(d.facets ?? emptyCatalogFacets);
    } catch (e: any) {
      setError(e.message || 'Error al cargar los ítems del catálogo');
    } finally {
      setCatalogBusy(false);
    }
  }

  function setCatalogFilter(key: string, value: string) {
    setCatalogFilters((f) => ({ ...f, [key]: value }));
  }

  // Con retraso: filtrar por texto dispararía una consulta por cada tecla.
  useEffect(() => {
    if (catalogOpen === null) return;
    const t = setTimeout(() => { void loadCatalogItems(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogFilters, catalogOpen]);

  function openCatalogItems(verticalCode: string) {
    setCatalogFilters({ ...emptyCatalogFilters, verticalCode });
    setCatalogOpen(verticalCode);
    void loadCatalogItems();
  }

   async function createTenant(e: React.FormEvent) {
     e.preventDefault();
     setError('');
     if (!newTenantForm.name.trim() || !newTenantForm.slug.trim()) {
       setError('Nombre y slug son requeridos');
       return;
     }
     setNewTenantBusy(true);
     try {
       const res = await saFetch('/sysadmin/tenants', {
         method: 'POST',
         body: JSON.stringify(newTenantForm),
       });
       const d = await res.json().catch(() => ({}));
       if (!res.ok) throw new Error(d.error || 'Error al crear comercio');
       setShowNewTenant(false);
       setNewTenantForm({ name: '', slug: '', taxRate: '21.00', currency: 'ARS' });
       loadTenants();
     } catch (e2: any) {
       setError(e2.message || 'Error');
     } finally {
       setNewTenantBusy(false);
     }
   }

  async function openTenant(t: any) {
    setSelected(t);
    setEditingId(null);
    setForm({ email: '', name: '', role: 'cashier', password: '', google: false, phone: '' });
    setResetPw('');
    setError('');
    try {
      const res = await saFetch(`/sysadmin/tenants/${t.id}/users`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al cargar usuarios');
      setUsers(d.data || []);
    } catch (e: any) {
      setError(e.message || 'Error');
      setUsers([]);
    }
  }

  async function createUser(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const body: any = {
        email: form.email, name: form.name || undefined,
        role: form.role,
        google: form.google,
        phone: form.phone || undefined,
      };
      if (!form.google) body.password = form.password;
      const res = await saFetch(`/sysadmin/tenants/${selected.id}/users`, { method: 'POST', body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al crear usuario');
      setForm({ email: '', name: '', role: 'cashier', password: '', google: false, phone: '' });
      openTenant(selected);
      loadTenants();
    } catch (e2: any) {
      setError(e2.message || 'Error');
    } finally {
      setBusy(false);
    }
  }

  async function updateUser(u: any, patch: any) {
    if (!selected) return;
    setError('');
    try {
      const res = await saFetch(`/sysadmin/tenants/${selected.id}/users/${u.id}`, { method: 'PUT', body: JSON.stringify(patch) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al actualizar usuario');
      openTenant(selected);
    } catch (e: any) {
      setError(e.message || 'Error');
    }
  }

  const ROLE_LABEL: Record<string, string> = { admin: 'Dueño', manager: 'Manager', cashier: 'Cajero' };

  if (catalogs) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
        <aside className="w-60 bg-gray-900 text-white flex flex-col shrink-0">
          <div className="p-5 border-b border-gray-700">
            <h1 className="font-bold">🛠️ SysAdmin</h1>
            <p className="text-xs text-gray-400 mt-1">{user?.email}</p>
          </div>
          <nav className="p-3 space-y-1 flex-1">
            <button onClick={() => { setCatalogs(null); setAuditLogs(null); setSelected(null); loadTenants(); }} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              🏪 Comercios
            </button>
            <button onClick={() => { setCatalogs(null); openAudit(); }} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              🔐 Auditoría
            </button>
            <div className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white font-medium">
              📚 Catálogos
            </div>
          </nav>
          <div className="p-4 border-t border-gray-700">
            <AlertPhoneCard />
            <button onClick={logout} className="w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">🚪 Salir</button>
          </div>
        </aside>

        <main className="flex-1 p-6 overflow-y-auto">
          <h1 className="text-2xl font-bold mb-1">Catálogos generales</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Bases de productos por rubro. Los comercios las leen y copian: son de solo lectura para ellos.
          </p>

          {error && <p className="text-sm text-red-600 dark:text-red-400 mb-4">⚠️ {error}</p>}

          {catalogOpen !== null ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                    Productos del catálogo · <span className="font-mono">{catalogOpen}</span>
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {catalogBusy ? 'Cargando…' : `${catalogTotal} ítem(s) con estos filtros`}
                  </p>
                </div>
                <button
                  onClick={() => setCatalogOpen(null)}
                  className="px-3 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  ← Volver a los catálogos
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                <input value={catalogFilters.q} onChange={(e) => setCatalogFilter('q', e.target.value)} placeholder="Todos los campos" className={saInput} />
                <input value={catalogFilters.gtin} onChange={(e) => setCatalogFilter('gtin', e.target.value)} placeholder="GTIN / código" className={saInput} />
                <input value={catalogFilters.sku} onChange={(e) => setCatalogFilter('sku', e.target.value)} placeholder="SKU" className={saInput} />
                <input value={catalogFilters.name} onChange={(e) => setCatalogFilter('name', e.target.value)} placeholder="Nombre" className={saInput} />
                <input value={catalogFilters.brand} onChange={(e) => setCatalogFilter('brand', e.target.value)} placeholder="Marca" className={saInput} />
                <select value={catalogFilters.category} onChange={(e) => setCatalogFilter('category', e.target.value)} className={saInput}>
                  <option value="">Toda categoría</option>
                  {catalogFacets.categories.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <select value={catalogFilters.saleUnit} onChange={(e) => setCatalogFilter('saleUnit', e.target.value)} className={saInput}>
                  <option value="">Unidad de venta</option>
                  {catalogFacets.saleUnits.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <select value={catalogFilters.purchaseUnit} onChange={(e) => setCatalogFilter('purchaseUnit', e.target.value)} className={saInput}>
                  <option value="">Unidad de compra</option>
                  {catalogFacets.purchaseUnits.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <select value={catalogFilters.active} onChange={(e) => setCatalogFilter('active', e.target.value)} className={saInput}>
                  <option value="">Activos e inactivos</option>
                  <option value="true">Solo activos</option>
                  <option value="false">Solo inactivos</option>
                </select>
                <select
                  value={catalogFilters.verticalCode}
                  onChange={(e) => { setCatalogFilter('verticalCode', e.target.value); setCatalogOpen(e.target.value || null); }}
                  className={saInput}
                >
                  {catalogFacets.verticals.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs text-gray-500 dark:text-gray-400">
                      <th className="py-2 pr-3">GTIN</th>
                      <th className="py-2 pr-3">SKU</th>
                      <th className="py-2 pr-3">Nombre</th>
                      <th className="py-2 pr-3">Marca</th>
                      <th className="py-2 pr-3">Categoría</th>
                      <th className="py-2 pr-3">U. venta</th>
                      <th className="py-2 pr-3">U. compra</th>
                      <th className="py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogItems.map((it) => (
                      <tr key={it.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-1.5 pr-3 font-mono text-xs">{it.gtin || '—'}</td>
                        <td className="py-1.5 pr-3 font-mono text-xs">{it.sku || '—'}</td>
                        <td className="py-1.5 pr-3">{it.name}</td>
                        <td className="py-1.5 pr-3">{it.brand || '—'}</td>
                        <td className="py-1.5 pr-3">{it.category_name || '—'}</td>
                        <td className="py-1.5 pr-3">{it.sale_unit || '—'}</td>
                        <td className="py-1.5 pr-3">{it.purchase_unit || '—'}</td>
                        <td className="py-1.5">{it.is_active ? 'activo' : 'inactivo'}</td>
                      </tr>
                    ))}
                    {!catalogItems.length && !catalogBusy && (
                      <tr>
                        <td colSpan={8} className="py-6 text-center text-gray-500 dark:text-gray-400">
                          Ningún ítem coincide con esos filtros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : catalogs.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              Todavía no hay catálogos cargados.
            </p>
          ) : (
            <div className="space-y-4">
              {catalogs.map((c) => (
                <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{c.name}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        código <span className="font-mono">{c.code}</span>
                        {c.provider ? ` · proveedor: ${c.provider}` : ''}
                        {c.active ? '' : ' · INACTIVO'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        c.version_label
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                      }`}
                    >
                      {c.version_label ? `publicado · ${c.version_label}` : 'sin publicar'}
                    </span>
                    <button
                      onClick={() => openCatalogItems(c.code)}
                      className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Ver productos ({c.published_items})
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Ítems vigentes</p>
                      <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">{c.published_items}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Comercios usándolo</p>
                      <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">{c.tenants_using}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Versiones</p>
                      <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">{c.versions}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Publicado</p>
                      <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {c.published_at ? new Date(c.published_at).toLocaleDateString('es-AR') : '—'}
                      </p>
                    </div>
                  </div>

                  {c.version_label && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      Última importación: {c.filename || '—'} · {c.row_count ?? 0} filas ·{' '}
                      {c.rejected_count ?? 0} rechazadas · {c.duplicate_count ?? 0} duplicadas
                    </p>
                  )}
                  {c.license_notes && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Licencia: {c.license_notes}</p>
                  )}

                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
                    Para importar o actualizar:{' '}
                    <span className="font-mono">
                      pnpm --filter core run catalog:import -- {c.code} archivo.csv --publish
                    </span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  if (auditLogs && !selected) {
    const METHOD_LABEL: Record<string, string> = {
      password: '🔑 Password', google: '🅖 Google', sysadmin: '🛠️ SysAdmin', refresh: '🔄 Refresh', logout: '🚪 Logout',
    };
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
        <aside className="w-60 bg-gray-900 text-white flex flex-col shrink-0">
          <div className="p-5 border-b border-gray-700">
            <h1 className="font-bold">🛠️ SysAdmin</h1>
            <p className="text-xs text-gray-400 mt-1">{user?.email}</p>
          </div>
          <nav className="p-3 space-y-1 flex-1">
            <button onClick={() => { setAuditLogs(null); setSelected(null); loadTenants(); }} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              🏪 Comercios
            </button>
            <div className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white font-medium">
              🔐 Auditoría
            </div>
            <button onClick={loadCatalogs} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              📚 Catálogos
            </button>
          </nav>
          <div className="p-4 border-t border-gray-700">
            <AlertPhoneCard />
            <button onClick={logout} className="w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">🚪 Salir</button>
          </div>
        </aside>

        <main className="flex-1 p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold">🔐 Auditoría de acceso</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Intentos de login, refresh y logout con su IP real</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={auditDays}
                onChange={(e) => { setAuditDays(Number(e.target.value)); loadAudit(); }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              >
                <option value={7}>Últimos 7 días</option>
                <option value={30}>Últimos 30 días</option>
                <option value={90}>Últimos 90 días</option>
              </select>
              <button onClick={loadAudit} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">↻ Refrescar</button>
            </div>
          </div>

          {error && <div className="bg-red-50 text-red-700 dark:text-red-300 p-3 rounded-lg mb-4 text-sm">⚠️ {error}</div>}

          {auditLoading ? (
            <p className="text-gray-500">Cargando...</p>
          ) : auditLogs.length === 0 ? (
            <p className="text-gray-500">Sin actividad de acceso en el período.</p>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-3 px-4">Método</th>
                    <th className="py-3 px-4">Email</th>
                    <th className="py-3 px-4">Comercio</th>
                    <th className="py-3 px-4">IP</th>
                    <th className="py-3 px-4">Resultado</th>
                    <th className="py-3 px-4">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((l) => (
                    <tr key={l.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <td className="py-2.5 px-4 whitespace-nowrap">{METHOD_LABEL[l.method] || l.method}</td>
                      <td className="py-2.5 px-4">{l.email || '—'}</td>
                      <td className="py-2.5 px-4">{l.tenantSlug || '—'}</td>
                      <td className="py-2.5 px-4 font-mono text-xs">{l.ip}</td>
                      <td className="py-2.5 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${l.success ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
                          {l.success ? '✓ Éxito' : '✗ Fallo'}
                        </span>
                        {l.detail && <span className="ml-2 text-xs text-gray-400">{l.detail}</span>}
                      </td>
                      <td className="py-2.5 px-4 whitespace-nowrap text-gray-500">{new Date(l.createdAt).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'medium' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
        {/* SysAdmin sidebar */}
        <aside className="w-60 bg-gray-900 text-white flex flex-col shrink-0">
          <div className="p-5 border-b border-gray-700">
            <h1 className="font-bold">🛠️ SysAdmin</h1>
            <p className="text-xs text-gray-400 mt-1">{user?.email}</p>
          </div>
          <nav className="p-3 space-y-1 flex-1">
            <button onClick={() => { setSelected(null); loadTenants(); }} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              🏪 Comercios
            </button>
            <button onClick={openAudit} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              🔐 Auditoría
            </button>
            <button onClick={loadCatalogs} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
              📚 Catálogos
            </button>
            <div className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white font-medium truncate" title={selected.name}>
              👥 {selected.name}
            </div>
          </nav>
          <div className="p-4 border-t border-gray-700">
            <AlertPhoneCard />
            <button onClick={logout} className="w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">🚪 Salir</button>
          </div>
        </aside>

        <main className="flex-1 p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Usuarios de {selected.name}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">slug: {selected.slug} · {users.length} usuario{users.length !== 1 ? 's' : ''}</p>
            </div>
            <button onClick={() => loadTenants()} className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-sm">↩ Volver a comercios</button>
          </div>

          {error && <div className="bg-red-50 text-red-700 dark:text-red-300 p-3 rounded-lg mb-4 text-sm">⚠️ {error}</div>}

          {/* Create user */}
          <form onSubmit={createUser} className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-5">
            <h3 className="font-semibold mb-3">＋ Nuevo usuario</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <input
                type="email" required placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              />
              <input
                type="text" placeholder="Nombre" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              />
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              >
                <option value="cashier">Cajero</option>
                <option value="manager">Manager</option>
                <option value="admin">Dueño</option>
              </select>
              <input
                type="tel" placeholder="Teléfono (WhatsApp)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              />
              {form.google ? (
                <div className="flex items-center justify-between px-3 py-2 border border-dashed border-green-500 rounded-lg text-sm text-green-600 dark:text-green-400">
                  <span>🔑 Google Workspace</span>
                  <span className="text-xs">sin password</span>
                </div>
              ) : (
                <input
                  type="password" placeholder="Password (mín 6)" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                />
              )}
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.google} onChange={(e) => setForm({ ...form, google: e.target.checked })} className="h-4 w-4" />
                Cuenta Google Workspace (valida contra Google OAuth, sin password)
              </label>
              <button disabled={busy} className="ml-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold min-h-[44px]">
                {busy ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </form>

          {/* Users table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Usuario</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Rol</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Estado</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-2.5">
                      <p className="font-medium">{u.name || '—'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{u.email}</p>
                      {u.phone && <p className="text-xs text-gray-500 dark:text-gray-400">📱 {u.phone}</p>}
                    </td>
                    <td className="px-4 py-2.5">
                      <select
                        value={u.role}
                        onChange={(e) => updateUser(u, { role: e.target.value })}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-xs"
                      >
                        <option value="admin">Dueño</option>
                        <option value="manager">Manager</option>
                        <option value="cashier">Cajero</option>
                      </select>
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => updateUser(u, { active: !u.active })}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium min-h-[36px] ${u.active ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}
                      >
                        {u.active ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      {editingId === u.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="password" placeholder="Nueva password" value={resetPw}
                            onChange={(e) => setResetPw(e.target.value)}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-xs w-36"
                          />
                          <button
                            onClick={() => { if (resetPw.length >= 6) { updateUser(u, { password: resetPw }); setEditingId(null); setResetPw(''); } }}
                            className="px-2 py-1 bg-blue-600 text-white rounded text-xs min-h-[36px]"
                          >
                            OK
                          </button>
                          <button onClick={() => { setEditingId(null); setResetPw(''); }} className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs min-h-[36px]">✕</button>
                        </div>
                      ) : (
                        <button onClick={() => setEditingId(u.id)} className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs font-medium">
                          🔑 Reset password
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
      {/* SysAdmin sidebar */}
      <aside className="w-60 bg-gray-900 text-white flex flex-col shrink-0">
        <div className="p-5 border-b border-gray-700">
          <h1 className="font-bold">🛠️ SysAdmin</h1>
          <p className="text-xs text-gray-400 mt-1">{user?.email}</p>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          <div className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white font-medium">🏪 Comercios</div>
          <button onClick={openAudit} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
            🔐 Auditoría
          </button>
          <button onClick={loadCatalogs} className="w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800">
            📚 Catálogos
          </button>
        </nav>
        <div className="p-4 border-t border-gray-700">
          <a href="/" className="block w-full text-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg mb-1">← App del comercio</a>
          <AlertPhoneCard />
          <button onClick={logout} className="w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">🚪 Salir</button>
        </div>
      </aside>

      <main className="flex-1 p-6 overflow-y-auto">
        <h1 className="text-2xl font-bold mb-1">Comercios</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">{tenants.length} comercio{tenants.length !== 1 ? 's' : ''} registrados</p>

         {!showNewTenant ? (
           <button onClick={() => setShowNewTenant(true)} className="mb-5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium">
             ➕ Nuevo comercio
           </button>
         ) : (
           <form onSubmit={createTenant} className="mb-5 bg-white dark:bg-gray-800 rounded-xl shadow p-5">
             <h3 className="font-semibold mb-3">➕ Nuevo comercio</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
               <div>
                 <label className="block text-sm font-medium mb-1">Nombre *</label>
                 <input
                   type="text"
                   required
                   value={newTenantForm.name}
                   onChange={(e) => setNewTenantForm({ ...newTenantForm, name: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                   placeholder="Ej: Mi Comercio Test"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium mb-1">Slug *</label>
                 <input
                   type="text"
                   required
                   value={newTenantForm.slug}
                   onChange={(e) => setNewTenantForm({ ...newTenantForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                   placeholder="mi-comercio-test"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium mb-1">IVA %</label>
                 <input
                   type="text"
                   value={newTenantForm.taxRate}
                   onChange={(e) => setNewTenantForm({ ...newTenantForm, taxRate: e.target.value })}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                   placeholder="21.00"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium mb-1">Moneda</label>
                 <input
                   type="text"
                   value={newTenantForm.currency}
                   onChange={(e) => setNewTenantForm({ ...newTenantForm, currency: e.target.value.toUpperCase() })}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                   placeholder="ARS"
                 />
               </div>
             </div>
             <div className="flex gap-3">
               <button type="submit" disabled={newTenantBusy} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium">
                 {newTenantBusy ? 'Creando...' : 'Crear comercio'}
               </button>
               <button type="button" onClick={() => { setShowNewTenant(false); setError(''); }} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-100 rounded-lg text-sm font-medium">
                 Cancelar
               </button>
             </div>
           </form>
         )}
 
         {error && <div className="bg-red-50 text-red-700 dark:text-red-300 p-3 rounded-lg mb-4 text-sm">⚠️ {error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {tenants.map((t) => (
            <button
              key={t.id}
              onClick={() => openTenant(t)}
              className="text-left bg-white dark:bg-gray-800 rounded-xl shadow p-5 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg">🏪 {t.name}</h3>
                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">{t.slug}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">IVA {t.taxRate}% · {t.currency} · Plan {t.plan}</p>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">{t.stats?.total ?? 0} usuarios</span>
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">{t.stats?.admins ?? 0} dueños</span>
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">{t.stats?.managers ?? 0} managers</span>
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">{t.stats?.cashiers ?? 0} cajeros</span>
              </div>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}